import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Select } from '@/ui'
import { ALLOCATION_SORT_KEYS, type AllocationSortKey } from './allocationSort.js'

const STORAGE_KEY = 'garage.storage.sort'
const DEFAULT_SORT: AllocationSortKey = 'name-asc'

function readSaved(): AllocationSortKey {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved && (ALLOCATION_SORT_KEYS as readonly string[]).includes(saved)) return saved as AllocationSortKey
  } catch {
    // Private mode / blocked storage: fall back to the default, never fail.
  }
  return DEFAULT_SORT
}

/** Remembers the chosen order between visits, so a shelf doesn't reset to A-Z every time. */
export function useAllocationSort(): [AllocationSortKey, (key: AllocationSortKey) => void] {
  const [sort, setSort] = useState<AllocationSortKey>(readSaved)
  const update = (key: AllocationSortKey) => {
    setSort(key)
    try {
      localStorage.setItem(STORAGE_KEY, key)
    } catch {
      // Not remembering is fine.
    }
  }
  return [sort, update]
}

export function AllocationSortSelect({
  value,
  onChange,
}: {
  value: AllocationSortKey
  onChange: (key: AllocationSortKey) => void
}) {
  const { t } = useTranslation()
  return (
    <Select
      label={t('storage.sort.label')}
      value={value}
      onChange={(v) => onChange(v as AllocationSortKey)}
      options={ALLOCATION_SORT_KEYS.map((k) => ({ value: k, label: t(`storage.sort.${k}`) }))}
      className="w-full sm:w-56"
    />
  )
}
